import { Client, ResourceId } from "../types/internal";
import { omit, pick } from "../utils/data";
import {
  AddressInput,
  Checkout,
  CheckoutBookingSlot,
  CheckoutCompleteInput,
  CheckoutContactUpdateInput,
  CheckoutCreateInput,
  CheckoutIntent,
  CheckoutPaymentOption,
  CheckoutUpdateInput,
  ShippingMethod,
} from "../models/Checkout";
import { getRouteQuery } from "../utils/route";
import { withQuery } from "ufo";

export const createCheckout = (client: Client) => {
  const update = async (token: string, data: CheckoutUpdateInput) => {
    const url = client.urls.checkouts(token);
    return client.post<Checkout>(url, { data });
  };

  return {
    get: (token: string) => {
      const url = client.urls.checkouts(token);
      return client.get<Checkout>(url);
    },

    create: (data: CheckoutCreateInput = {}) => {
      let successUrl = data.success_url;
      let cancelUrl = data.cancel_url;

      if (successUrl || cancelUrl) {
        const routeQuery = getRouteQuery();
        const params = omit(routeQuery, ["token"]);
        if (successUrl) successUrl = withQuery(successUrl, params);
        if (cancelUrl) cancelUrl = withQuery(cancelUrl, params);
      }

      const url = client.urls.checkouts();
      return client.post<Checkout>(url, {
        data: {
          ...data,
          success_url: successUrl,
          cancel_url: cancelUrl,
        },
      });
    },

    update,

    setContact: (token: string, data: CheckoutContactUpdateInput) => {
      const input = pick(data, ["first_name", "last_name", "email", "phone"]);
      return update(token, input);
    },

    setShippingAddress: (token: string, address: AddressInput) => {
      return update(token, {
        shipping_address: { set: address },
      });
    },

    setBillingAddress: (token: string, address: AddressInput) => {
      return update(token, {
        billing_address: { set: address },
      });
    },

    useShippingAddressAsBilling: (token: string) => {
      return update(token, {
        billing_address: { use_shipping: true },
      });
    },

    updateItemQuantity: (token: string, id: ResourceId, quantity: number) => {
      return update(token, {
        lines: {
          update: [{ id: String(id), quantity }],
        },
      });
    },

    applyPromocode: (token: string, code: string) => {
      return update(token, {
        discount: { set: [code] },
      });
    },

    removePromocode: (token: string) => {
      return update(token, {
        discount: { clear: true },
      });
    },

    selectShipping: (token: string, line: string) => {
      const url = client.urls.checkouts(token, "select-shipping");
      return client.post<Checkout>(url, {
        data: { line },
      });
    },

    complete: (token: string, data?: CheckoutCompleteInput) => {
      const url = client.urls.checkouts(token, "complete");
      return client.post<Checkout>(url, { data });
    },

    getShippingMethods: (token: string) => {
      const url = client.urls.checkouts(token, "shipping-methods");
      return client.get<ShippingMethod[]>(url);
    },

    getPaymentOptions: (token: string) => {
      const url = client.urls.checkouts(token, "payment-options");
      return client.get<CheckoutPaymentOption[]>(url);
    },

    getBookingSlots: (token: string) => {
      const url = client.urls.checkouts(token, "booking-slots");
      return client.get<CheckoutBookingSlot[]>(url);
    },

    intentCreate: (data: CheckoutCreateInput = {}) => {
      const url = client.urls.checkouts("intent");
      return client.post<CheckoutIntent>(url, { data });
    },

    intentUse: (token: string) => {
      const url = client.urls.checkouts(token, "intent-use");
      return client.post<Checkout>(url);
    },
  };
};
